import { GuidService } from 'ui-components/services/guid-service';
import type { OrderItem, Package, AssignedItem } from '../types';

type ItemChunk = {
  chunkId: string;
  quantity: number;
  packageIndex?: number;
  orderInPackage?: number;
};

export type ItemQuantities = {
  [itemId: string]: {
    total: number;
    chunks: ItemChunk[];
  };
};

export const generateChunkId = () => GuidService.newGuid();

export const initializeItemQuantities = (items: OrderItem[]): ItemQuantities => {
  return items.reduce(
    (acc, item) => ({
      ...acc,
      [item.fulfillmentPlanItemId]: {
        total: item.quantity,
        chunks: [{ chunkId: generateChunkId(), quantity: item.quantity }],
      },
    }),
    {},
  );
};

export const hasZeroQuantityItem = (items: OrderItem[]): boolean => items.some((item) => item.quantity === 0);

export const getUnassignedItems = (items: OrderItem[], itemQuantities: ItemQuantities) => {
  const result: Array<{ item: OrderItem; chunkId: string; quantity: number }> = [];

  items.forEach((item) => {
    const quantities = itemQuantities[item.fulfillmentPlanItemId];
    if (quantities) {
      quantities.chunks.forEach((chunk) => {
        if (!chunk.packageIndex && chunk.packageIndex !== 0) {
          result.push({ item, chunkId: chunk.chunkId, quantity: chunk.quantity });
        }
      });
    }
  });

  return result;
};

export const getAssignedItemsForPackage = (packageIndex: number, itemQuantities: ItemQuantities) => {
  const result: Array<{ itemId: string; chunkId: string; quantity: number; orderInPackage: number }> = [];

  Object.entries(itemQuantities).forEach(([itemId, quantities]) => {
    quantities.chunks.forEach((chunk) => {
      if (chunk.packageIndex === packageIndex) {
        result.push({
          itemId,
          chunkId: chunk.chunkId,
          quantity: chunk.quantity,
          orderInPackage: chunk.orderInPackage ?? 0,
        });
      }
    });
  });

  return result.sort((a, b) => a.orderInPackage - b.orderInPackage);
};

export const canAssignQuantity = (itemId: string, chunkId: string, itemQuantities: ItemQuantities): boolean => {
  const currentQuantities = itemQuantities[itemId];
  if (!currentQuantities) {
    return false;
  }
  const chunk = currentQuantities.chunks.find((c) => c.chunkId === chunkId);
  return !!chunk && !chunk.packageIndex && chunk.packageIndex !== 0;
};

export const getItemsAssignedCount = (itemQuantities: ItemQuantities): number => {
  return Object.values(itemQuantities).filter((quantities) => quantities.chunks.some((chunk) => chunk.packageIndex !== undefined)).length;
};

export const mergeChunksInPackage = (state: ItemQuantities, itemId: string, packageIndex: number): ItemQuantities => {
  const itemData = state[itemId];
  if (!itemData) return state;

  const chunksInPackage = itemData.chunks.filter((c) => c.packageIndex === packageIndex);
  if (chunksInPackage.length <= 1) return state;

  const totalQuantity = chunksInPackage.reduce((sum, c) => sum + c.quantity, 0);
  const [keepChunkId, ...removedIds] = chunksInPackage.map((c) => c.chunkId);
  const removed = new Set(removedIds);

  const chunks = itemData.chunks
    .filter((c) => !removed.has(c.chunkId))
    .map((c) => (c.chunkId === keepChunkId ? { ...c, quantity: totalQuantity } : c));

  return { ...state, [itemId]: { ...itemData, chunks } };
};

export const convertToAssignments = (itemQuantities: ItemQuantities, packages: Package[]): Package[] => {
  const updatedPackages = packages.map((pkg, packageIndex) => {
    const assignedItems: AssignedItem[] = [];

    Object.entries(itemQuantities).forEach(([itemId, quantities]) => {
      quantities.chunks.forEach((chunk) => {
        if (chunk.packageIndex === packageIndex) {
          assignedItems.push({
            orderItemId: itemId,
            quantity: chunk.quantity,
          });
        }
      });
    });

    return {
      ...pkg,
      assignedItems,
    };
  });

  return updatedPackages;
};

export const getMaxQuantityForChunk = (itemId: string, chunkId: string, itemQuantities: ItemQuantities): number => {
  const currentQuantities = itemQuantities[itemId];
  if (!currentQuantities) {
    return 1;
  }

  const currentChunk = currentQuantities.chunks.find((c) => c.chunkId === chunkId);
  if (!currentChunk) {
    return 1;
  }

  let maxQuantity = currentChunk.quantity;

  currentQuantities.chunks.forEach((chunk) => {
    if (chunk.chunkId !== chunkId && chunk.packageIndex === undefined) {
      maxQuantity += chunk.quantity;
    }
  });

  return maxQuantity;
};

export const hasUnassignedItems = (items: OrderItem[], itemQuantities: ItemQuantities): boolean =>
  getUnassignedItems(items, itemQuantities).length > 0;

export const assignUnassignedToFirstPackage = (itemQuantities: ItemQuantities): ItemQuantities => {
  let updated = { ...itemQuantities };

  Object.keys(updated).forEach((itemId) => {
    const entry = updated[itemId];
    const hasUnassigned = entry.chunks.some(({ packageIndex }) => packageIndex === undefined);
    if (!hasUnassigned) return;

    let orderInPackage = entry.chunks.filter(({ packageIndex }) => packageIndex === 0).length;

    updated[itemId] = {
      ...entry,
      chunks: entry.chunks.map((chunk) => {
        if (chunk.packageIndex !== undefined) return chunk;
        return { ...chunk, packageIndex: 0, orderInPackage: orderInPackage++ };
      }),
    };

    updated = mergeChunksInPackage(updated, itemId, 0);
  });

  return updated;
};

export const convertFromAssignments = (items: OrderItem[], packages: Package[]): ItemQuantities => {
  const quantities: ItemQuantities = {};

  items.forEach((item) => {
    const itemId = item.fulfillmentPlanItemId;
    const chunks: ItemChunk[] = [];

    packages.forEach((pkg, packageIndex) => {
      const assignedItems = pkg.assignedItems?.filter((ai) => ai.orderItemId === itemId) ?? [];
      assignedItems.forEach((assignedItem) => {
        chunks.push({
          chunkId: generateChunkId(),
          quantity: assignedItem.quantity,
          packageIndex,
          orderInPackage: chunks.filter((c) => c.packageIndex === packageIndex).length,
        });
      });
    });

    const assignedTotal = chunks.reduce((sum, chunk) => sum + chunk.quantity, 0);

    if (assignedTotal < item.quantity) {
      chunks.push({
        chunkId: generateChunkId(),
        quantity: item.quantity - assignedTotal,
      });
    }

    quantities[itemId] = {
      total: item.quantity,
      chunks,
    };
  });

  return quantities;
};
